(() => {
  "use strict";

  const SINK_NAME = "hooksole-newsletter-sink";
  const STORAGE_DISMISSED_UNTIL = "hooksole-newsletter-dismissed-until";
  const STORAGE_SUBSCRIBED = "hooksole-newsletter-subscribed";
  const DISMISS_DAYS = 7;
  const AUTO_OPEN_DELAY = 15000;
  const SUBMIT_TIMEOUT = 60000;
  const SUCCESS_MESSAGE = "Thanks — you’re subscribed. We’ll send occasional studio notes and restock updates.";

  const hasStorage = () => {
    try {
      const key = "__hooksole_newsletter_test__";
      window.localStorage.setItem(key, "1");
      window.localStorage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  };

  const storage = {
    get(key) {
      if (!hasStorage()) return null;
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      if (!hasStorage()) return;
      try {
        window.localStorage.setItem(key, String(value));
      } catch (error) {
        // Storage can be unavailable in strict privacy modes.
      }
    },
  };

  const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim();

  const classifyNewsletterResponse = (documentNode) => {
    const text = normalizeText(documentNode?.body?.innerText).slice(0, 20000);
    const lower = text.toLowerCase();

    if (!text) return { status: "unknown", message: "" };

    if (
      /missing captcha|captcha (?:token|verification|failed)|verifying your connection|checking your browser/i.test(
        lower
      )
    ) {
      return {
        status: "error",
        message: "Please complete the security check, then try subscribing again.",
      };
    }

    if (
      /(?:this site can’t be reached|this site can't be reached|net::err_|temporarily unavailable|something went wrong)/i.test(
        lower
      )
    ) {
      return {
        status: "error",
        message: "We couldn’t connect just now. Please check your connection and try again.",
      };
    }

    if (
      /(?:thank you|thanks[ —,:.-]*(?:you|for)|you(?:'|’)re on the list|you are on the list|successfully subscribed|subscribed successfully)/i.test(
        text
      )
    ) {
      return { status: "success", message: SUCCESS_MESSAGE };
    }

    const explicitError = documentNode?.querySelector(
      "#ContactFooter-error, [id$='-error'], [id*='-error--'], .newsletter-form__message:not(.newsletter-form__message--success)"
    );
    if (explicitError && normalizeText(explicitError.innerText)) {
      return {
        status: "error",
        message: normalizeText(explicitError.innerText).slice(0, 180),
      };
    }

    if (/(?:not a valid email|valid email address|too many requests|please try again)/i.test(lower)) {
      return {
        status: "error",
        message: "Please check the email address and try again.",
      };
    }

    // Shopify may render a normal contact page after a successful customer-form post.
    // Reaching that page without an error means the subscription was accepted.
    if (documentNode?.querySelector("form input[name='contact[email]']")) {
      return { status: "success", message: SUCCESS_MESSAGE };
    }

    return { status: "unknown", message: "" };
  };

  if (window.name === SINK_NAME) {
    const reportResult = () => {
      const result = classifyNewsletterResponse(document);
      if (result.status === "unknown") return;
      try {
        window.parent.postMessage(
          {
            type: "hooksole-newsletter-status",
            status: result.status,
            message: result.message,
          },
          window.location.origin
        );
      } catch (error) {
        // The parent will also inspect the same-origin iframe as a fallback.
      }
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", reportResult, { once: true });
    } else {
      reportResult();
    }
    return;
  }

  const isNewsletterForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    const formType = form.querySelector("input[name='form_type']");
    const email = form.querySelector("input[name='contact[email]'][type='email']");
    return Boolean(email && (!formType || formType.value === "customer"));
  };

  const getEmail = (form) =>
    String(form?.querySelector("input[name='contact[email]']")?.value || "").trim();

  let overlay;
  let formView;
  let successView;
  let popupForm;
  let popupEmail;
  let popupSubmit;
  let feedback;
  let previousFocus = null;
  let currentMode = "form";
  let closeTimer = null;
  let pendingSubmission = null;
  let popupDismissed = false;

  const makeElement = (tag, attributes = {}, children = []) => {
    const element = document.createElement(tag);
    Object.entries(attributes).forEach(([key, value]) => {
      if (key === "className") element.className = value;
      else if (key === "textContent") element.textContent = value;
      else element.setAttribute(key, value);
    });
    children.forEach((child) => element.append(child));
    return element;
  };

  const createPopupMarkup = () => {
    if (document.querySelector(".hs-newsletter-popup")) return;

    const closeIcon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    closeIcon.setAttribute("viewBox", "0 0 20 20");
    closeIcon.setAttribute("aria-hidden", "true");
    closeIcon.innerHTML = '<path d="M4 4l12 12M16 4L4 16" fill="none" stroke="currentColor" stroke-width="1.5"/>';

    const closeButton = makeElement(
      "button",
      {
        type: "button",
        className: "hs-newsletter-popup__close",
        "aria-label": "Close newsletter signup",
        "data-hs-np-close": "",
      },
      [closeIcon]
    );

    popupEmail = makeElement("input", {
      type: "email",
      name: "contact[email]",
      id: "HooksoleNewsletterPopupEmail",
      autocomplete: "email",
      autocapitalize: "off",
      autocorrect: "off",
      placeholder: "Your email address",
      "aria-label": "Your email address",
      required: "",
    });

    popupSubmit = makeElement("button", {
      type: "submit",
      className: "hs-newsletter-popup__submit",
      textContent: "Join the list",
    });

    feedback = makeElement("p", {
      className: "hs-newsletter-popup__feedback",
      role: "alert",
      "aria-live": "polite",
    });

    popupForm = makeElement(
      "form",
      {
        method: "post",
        action: "/contact#contact_form",
        acceptCharset: "UTF-8",
        className: "hs-newsletter-popup__form",
        id: "HooksoleNewsletterPopupForm",
      },
      [
        makeElement("input", { type: "hidden", name: "form_type", value: "customer" }),
        makeElement("input", { type: "hidden", name: "utf8", value: "✓" }),
        makeElement("input", { type: "hidden", name: "contact[tags]", value: "newsletter" }),
        makeElement("div", { className: "hs-newsletter-popup__row" }, [popupEmail, popupSubmit]),
        feedback,
      ]
    );

    const benefitItems = [
      ["01", "First access", "Meet new colours before they sell out."],
      ["02", "Restock notes", "Know when your favourite pair returns."],
      ["03", "Studio journal", "Styling ideas, care tips, and maker stories."],
    ].map(([number, title, text]) =>
      makeElement("li", { className: "hs-newsletter-popup__benefit" }, [
        makeElement("span", {
          className: "hs-newsletter-popup__benefit-number",
          textContent: number,
        }),
        makeElement("span", { className: "hs-newsletter-popup__benefit-copy" }, [
          makeElement("strong", { textContent: title }),
          makeElement("span", { textContent: text }),
        ]),
      ])
    );

    formView = makeElement("div", { className: "hs-newsletter-popup__form-view" }, [
      makeElement("p", {
        className: "hs-newsletter-popup__eyebrow",
        textContent: "The Hooksole letter",
      }),
      makeElement("h2", {
        id: "HooksoleNewsletterTitle",
        textContent: "Good things, delivered softly.",
      }),
      makeElement("p", {
        className: "hs-newsletter-popup__copy",
        textContent:
          "Be first to discover new colours, limited restocks, and thoughtful notes from our studio.",
      }),
      makeElement("ul", {
        className: "hs-newsletter-popup__benefits",
        "aria-label": "What you will receive",
      }, benefitItems),
      popupForm,
      makeElement("p", { className: "hs-newsletter-popup__fine" }, [
        document.createTextNode("No noise — one thoughtful email a month. By joining, you agree to our "),
        makeElement("a", {
          href: "/policies/privacy-policy",
          textContent: "Privacy Policy",
        }),
        document.createTextNode(". Unsubscribe anytime."),
      ]),
    ]);

    const visual = makeElement("div", {
      className: "hs-newsletter-popup__visual",
      "aria-hidden": "true",
    }, [
      makeElement("span", {
        className: "hs-newsletter-popup__brand-logo",
      }),
    ]);

    const check = makeElement("span", { className: "hs-newsletter-popup__check", "aria-hidden": "true" });
    check.innerHTML =
      '<svg viewBox="0 0 28 28" focusable="false"><path d="M5 14.5l5.4 5.4L23 7.8" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    successView = makeElement("div", { className: "hs-newsletter-popup__success", hidden: "" }, [
      check,
      makeElement("p", {
        className: "hs-newsletter-popup__eyebrow",
        textContent: "Welcome to the Hooksole letter",
      }),
      makeElement("h2", {
        id: "HooksoleNewsletterSuccessTitle",
        tabindex: "-1",
        textContent: "Consider it sent.",
      }),
      makeElement("p", {
        className: "hs-newsletter-popup__copy",
        textContent:
          "You’re on the list. Watch your inbox for first looks, restock notes, and stories from the studio.",
      }),
      makeElement("button", {
        type: "button",
        className: "hs-newsletter-popup__continue",
        textContent: "Keep exploring",
        "data-hs-np-close": "",
      }),
    ]);

    const content = makeElement("div", {
      className: "hs-newsletter-popup__content",
    }, [
      formView,
      successView,
    ]);
    const inner = makeElement("div", { className: "hs-newsletter-popup__inner" }, [
      visual,
      content,
    ]);
    const panel = makeElement("div", {
      className: "hs-newsletter-popup__panel",
      role: "document",
    }, [inner, closeButton]);

    overlay = makeElement("div", {
      className: "hs-newsletter-popup",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": "HooksoleNewsletterTitle",
      "aria-hidden": "true",
      hidden: "",
    }, [panel]);

    overlay.addEventListener("click", (event) => {
      if (event.target === overlay || event.target.closest("[data-hs-np-close]")) {
        closePopup();
      }
    });

    overlay.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closePopup();
        return;
      }

      if (event.key !== "Tab") return;
      const focusable = [
        ...overlay.querySelectorAll(
          "button:not([disabled]), input:not([disabled]):not([type='hidden']), a[href]"
        ),
      ].filter((element) => !element.closest("[hidden]") && element.getClientRects().length);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.body.append(overlay);
  };

  const lockPage = () => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.classList.add("hs-newsletter-popup-open");
    if (scrollbarWidth > 0) {
      document.body.style.setProperty("--hs-np-scrollbar-gap", `${scrollbarWidth}px`);
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  };

  const unlockPage = () => {
    document.body.classList.remove("hs-newsletter-popup-open");
    document.body.style.removeProperty("--hs-np-scrollbar-gap");
    document.body.style.paddingRight = "";
  };

  const resetFormState = () => {
    if (!popupForm) return;
    popupForm.reset();
    feedback.textContent = "";
    popupEmail.removeAttribute("aria-invalid");
    popupSubmit.disabled = false;
    popupSubmit.textContent = "Join the list";
  };

  const openPopup = ({ mode = "form" } = {}) => {
    if (!overlay) return;
    clearTimeout(closeTimer);
    previousFocus = document.activeElement;
    currentMode = mode;
    const success = mode === "success";
    formView.hidden = success;
    successView.hidden = !success;
    if (!success) resetFormState();

    overlay.dataset.mode = mode;
    overlay.setAttribute(
      "aria-labelledby",
      success ? "HooksoleNewsletterSuccessTitle" : "HooksoleNewsletterTitle"
    );
    overlay.hidden = false;
    overlay.setAttribute("aria-hidden", "false");
    lockPage();

    requestAnimationFrame(() => {
      overlay.classList.add("is-open");
      const target = success
        ? successView.querySelector("h2")
        : popupEmail;
      window.setTimeout(() => target?.focus({ preventScroll: true }), 80);
    });
  };

  const closePopup = ({ remember = true } = {}) => {
    if (!overlay || overlay.hidden) return;

    if (remember && currentMode !== "success") {
      const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
      storage.set(STORAGE_DISMISSED_UNTIL, until);
      popupDismissed = true;
    }

    overlay.classList.remove("is-open");
    overlay.setAttribute("aria-hidden", "true");
    unlockPage();
    closeTimer = window.setTimeout(() => {
      overlay.hidden = true;
      if (previousFocus instanceof HTMLElement) previousFocus.focus({ preventScroll: true });
      previousFocus = null;
    }, 230);
  };

  const showSuccess = ({ remember = true } = {}) => {
    if (remember) {
      storage.set(STORAGE_SUBSCRIBED, "1");
      try {
        window.localStorage.removeItem(STORAGE_DISMISSED_UNTIL);
      } catch (error) {
        // Ignore storage restrictions.
      }
    }
    openPopup({ mode: "success" });
  };

  const setButtonBusy = (button, busy, success = false) => {
    if (!button) return;
    if (busy) {
      if (!button.dataset.hsOriginalLabel) {
        button.dataset.hsOriginalLabel = button.textContent;
      }
      button.disabled = true;
      button.textContent = "Joining…";
    } else {
      button.disabled = false;
      button.textContent = success
        ? "Subscribed"
        : button.dataset.hsOriginalLabel || button.textContent;
    }
  };

  const removeInlineMessage = (form) => {
    form.parentElement?.querySelectorAll(".hs-np-inline-message").forEach((node) => node.remove());
  };

  const showInlineMessage = (form, message, isError = false) => {
    removeInlineMessage(form);
    const node = makeElement("p", {
      className: `hs-np-inline-message${isError ? " is-error" : ""}`,
      role: isError ? "alert" : "status",
      textContent: message,
    });
    form.insertAdjacentElement("afterend", node);
  };

  const finishSubmission = (result) => {
    if (!pendingSubmission) return;
    const { form, button, email, timeoutId, source } = pendingSubmission;
    clearTimeout(timeoutId);
    pendingSubmission = null;

    if (result.status === "success") {
      setButtonBusy(button, false, true);
      showInlineMessage(form, "Thanks — you’re subscribed.");
      if (source === "popup") {
        popupSubmit.disabled = false;
        popupSubmit.textContent = "Subscribed";
        feedback.textContent = "";
      }
      showSuccess();
      return;
    }

    const message =
      result.message || "We couldn’t confirm your subscription. Please try again in a moment.";
    setButtonBusy(button, false);
    if (source === "popup") {
      popupSubmit.disabled = false;
      popupSubmit.textContent = "Join the list";
      feedback.textContent = message;
      popupEmail.setAttribute("aria-invalid", "true");
      popupEmail.focus({ preventScroll: true });
    } else {
      showInlineMessage(form, message, true);
      form.querySelector("input[name='contact[email]']")?.focus({ preventScroll: true });
    }
    if (email) form.dataset.hsNewsletterEmail = email;
  };

  const inspectSink = () => {
    if (!pendingSubmission || !sink) return;
    let documentNode;
    try {
      documentNode = sink.contentDocument;
      if (!documentNode?.body || documentNode.readyState !== "complete") return;
    } catch (error) {
      return;
    }
    const result = classifyNewsletterResponse(documentNode);
    if (result.status !== "unknown") finishSubmission(result);
  };

  const createSink = () => {
    if (sink) return sink;
    sink = makeElement("iframe", {
      name: SINK_NAME,
      title: "Newsletter subscription response",
      tabindex: "-1",
      "aria-hidden": "true",
    });
    document.body.append(sink);
    sink.addEventListener("load", () => {
      window.setTimeout(inspectSink, 250);
    });
    return sink;
  };

  let sink;

  const beginSubmission = (form, source) => {
    if (source !== "popup") {
      removeInlineMessage(form);
      form.querySelector("input[name='contact[email]']")?.removeAttribute("aria-invalid");
    } else {
      feedback.textContent = "";
      popupEmail.removeAttribute("aria-invalid");
      popupSubmit.textContent = "Joining…";
    }

    const email = getEmail(form);
    form.dataset.hsNewsletterEmail = email;
    const button = form.querySelector(
      source === "popup" ? ".hs-newsletter-popup__submit" : "button[type='submit']"
    );
    if (source !== "popup") setButtonBusy(button, true);
    else popupSubmit.disabled = true;

    const timeoutId = window.setTimeout(() => {
      finishSubmission({
        status: "error",
        message: "The subscription is taking longer than expected. Please try again.",
      });
    }, SUBMIT_TIMEOUT);

    pendingSubmission = { form, button, email, timeoutId, source };
  };

  const prepareForm = (form, source = "page") => {
    if (form.dataset.hsNewsletterBound === "true") return;
    form.dataset.hsNewsletterBound = "true";
    form.addEventListener("submit", (event) => {
      if (event.defaultPrevented) return;
      beginSubmission(form, source);
    });
  };

  const handlePostedSuccess = () => {
    let url;
    try {
      url = new URL(window.location.href);
    } catch (error) {
      return false;
    }
    if (url.searchParams.get("customer_posted") !== "true") return false;

    url.searchParams.delete("customer_posted");
    const nextUrl = `${url.pathname}${url.search}${url.hash || "#contact_form"}`;
    window.history.replaceState(window.history.state, "", nextUrl);
    storage.set(STORAGE_SUBSCRIBED, "1");
    try {
      window.localStorage.removeItem(STORAGE_DISMISSED_UNTIL);
    } catch (error) {
      // Ignore storage restrictions.
    }
    window.requestAnimationFrame(() => showSuccess());
    return true;
  };

  const showContactFeedback = () => {
    const successMessage = document.querySelector(
      ".hs-contact-form .hs-message--success"
    );
    if (!successMessage) return false;

    successMessage.hidden = true;
    const previouslyFocused = document.activeElement;
    const overlay = document.createElement("div");
    overlay.className = "hs-contact-feedback";
    overlay.hidden = true;
    overlay.innerHTML = `
      <section class="hs-contact-feedback__card" role="dialog" aria-modal="true" aria-labelledby="HooksoleContactSuccessTitle">
        <span class="hs-contact-feedback__icon" aria-hidden="true">✓</span>
        <p class="hs-contact-feedback__eyebrow">Message sent</p>
        <h2 id="HooksoleContactSuccessTitle">Thank you for reaching out.</h2>
        <p class="hs-contact-feedback__copy">Your message is on its way. We’ll get back to you within 1–2 business days.</p>
        <button class="hs-contact-feedback__close" type="button">Close</button>
      </section>
    `;
    document.body.appendChild(overlay);

    const closeButton = overlay.querySelector(".hs-contact-feedback__close");
    const close = () => {
      overlay.classList.remove("is-open");
      document.body.classList.remove("hs-contact-feedback-open");
      document.removeEventListener("keydown", onKeydown);
      window.setTimeout(() => {
        overlay.hidden = true;
      }, 220);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
    const onKeydown = (event) => {
      if (event.key === "Escape") close();
    };

    closeButton.addEventListener("click", close);
    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) close();
    });
    document.addEventListener("keydown", onKeydown);

    overlay.hidden = false;
    document.body.classList.add("hs-contact-feedback-open");
    const activateOverlay = () => {
      if (overlay.hidden) return;
      overlay.classList.add("is-open");
      closeButton.focus({ preventScroll: true });
    };
    window.requestAnimationFrame(activateOverlay);
    window.setTimeout(activateOverlay, 80);

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("contact_posted");
      window.history.replaceState(
        window.history.state,
        "",
        `${url.pathname}${url.search}${url.hash}`
      );
    } catch (error) {
      // Keep the original URL if the browser restricts history updates.
    }
    return true;
  };

  const showContactSuccess = () => {
    const form = document.querySelector(".hs-contact-form");
    if (!form) return false;
    if (!form.querySelector(".hs-message--success")) {
      const message = document.createElement("p");
      message.className = "hs-message hs-message--success";
      message.textContent = "Thanks — your message is on its way.";
      form.prepend(message);
    }
    return showContactFeedback();
  };

  const shouldAutoOpen = () => {
    if (popupDismissed || storage.get(STORAGE_SUBSCRIBED) === "1") return false;
    const until = Number(storage.get(STORAGE_DISMISSED_UNTIL) || 0);
    return !Number.isFinite(until) || Date.now() >= until;
  };

  const anotherOverlayIsOpen = () => {
    const candidates = [
      "#CartDrawer:not([hidden])",
      ".cart-drawer.is-open",
      "#search-modal[open]",
      ".search-modal[open]",
      ".hs-contact-feedback:not([hidden])",
      "dialog[open]",
    ];
    return candidates.some((selector) => {
      const element = document.querySelector(selector);
      return element && element.getClientRects().length > 0;
    });
  };

  const scheduleAutoOpen = () => {
    if (!shouldAutoOpen()) return;
    window.setTimeout(() => {
      if (document.visibilityState === "visible" && !anotherOverlayIsOpen()) {
        openPopup({ mode: "form" });
      }
    }, AUTO_OPEN_DELAY);
  };

  const init = () => {
    createPopupMarkup();
    showContactFeedback();

    const pageForms = [...document.querySelectorAll("form")].filter(isNewsletterForm);
    pageForms.forEach((form) => prepareForm(form));
    prepareForm(popupForm, "popup");

    if (handlePostedSuccess()) {
      document.documentElement.classList.add("hs-newsletter-popup-ready");
      return;
    }

    window.addEventListener("message", (event) => {
      if (!pendingSubmission || event.origin !== window.location.origin) return;
      if (event.source !== sink.contentWindow) return;
      const data = event.data;
      if (!data || data.type !== "hooksole-newsletter-status") return;
      if (data.status !== "success" && data.status !== "error") return;
      finishSubmission({ status: data.status, message: data.message || "" });
    });

    window.addEventListener("pageshow", (event) => {
      if (!event.persisted) return;
      overlay?.classList.remove("is-open");
      if (overlay) overlay.hidden = true;
      unlockPage();
    });

    scheduleAutoOpen();
    document.documentElement.classList.add("hs-newsletter-popup-ready");
  };

  window.HooksoleNewsletter = Object.freeze({
    open: () => openPopup({ mode: "form" }),
    showSuccess: () => showSuccess({ remember: false }),
    showContactSuccess,
    clearFrequencyCap: () => {
      try {
        window.localStorage.removeItem(STORAGE_DISMISSED_UNTIL);
        window.localStorage.removeItem(STORAGE_SUBSCRIBED);
      } catch (error) {
        // Ignore storage restrictions.
      }
    },
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
